import { useEffect, useRef, useState } from 'react';
import { MEETING_NOTES_MAX, type CalendarEventSummary, type MeetingFollowUpDraft } from '@todo/shared';
import { calendarApi } from '../api/calendar.api';
import { ApiError } from '../api/client';

/**
 * The meeting NOTEPAD and the follow-up draft built from it.
 *
 * Two things, one block, because they are one workflow: you write what
 * happened, then you send it on. Splitting them would mean writing notes in
 * one place and discovering the draft somewhere else.
 *
 * WHY THE NOTEPAD IS NOT THE DESCRIPTION: the description is the agenda,
 * written beforehand and already posted to everyone in the invite email.
 * Typing the minutes over it would rewrite history and give the follow-up
 * nothing to compare against.
 *
 * WHY MANUAL SAVE AND NOT AUTOSAVE-ON-KEYSTROKE: notes are pasted and
 * rewritten in bursts, and a per-keystroke PUT would have the server
 * storing half-sentences and racing itself. The button is enabled only
 * while there is an unsaved change, and Ctrl/Cmd+S works.
 *
 * THE DRAFT IS NEVER SENT. The API returns text; the person copies it. No
 * "send to attendees" button exists, on purpose - a one-click mail to
 * everyone from a screen you opened to read is how the wrong draft goes
 * out.
 */
export function MeetingNotes({
  event, canEdit, onSaved,
}: {
  event: CalendarEventSummary;
  /** True for the organiser - everyone else reads. */
  canEdit: boolean;
  onSaved: (updated: CalendarEventSummary) => void;
}) {
  const [text, setText] = useState(event.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<MeetingFollowUpDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);

  // Switching to another event in the same panel must not carry the
  // previous one's notes or draft across.
  useEffect(() => {
    setText(event.notes ?? '');
    setDraft(null);
    setError(null);
    setDraftError(null);
  }, [event.id, event.notes]);

  useEffect(() => () => {
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
  }, []);

  const dirty = text !== (event.notes ?? '');

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await calendarApi.saveNotes(event.id, text));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those notes did not save.');
    } finally {
      setSaving(false);
    }
  }

  async function makeDraft() {
    setDrafting(true);
    setDraftError(null);
    try {
      setDraft(await calendarApi.followUp(event.id));
    } catch (err) {
      setDraftError(
        err instanceof ApiError
          ? err.message
          : 'Could not draft a follow-up just now.',
      );
    } finally {
      setDrafting(false);
    }
  }

  function copy() {
    if (!draft) return;
    void navigator.clipboard
      .writeText(`${draft.subject}\n\n${draft.body}`)
      .then(() => {
        setCopied(true);
        if (copyTimer.current) window.clearTimeout(copyTimer.current);
        copyTimer.current = window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setDraftError('Could not copy - select the text and copy it by hand.'));
  }

  const savedLabel = event.notesUpdatedAt
    ? new Date(event.notesUpdatedAt).toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Notepad
        </p>
        <p className="text-[11px] text-slate-400">
          {savedLabel ? `Saved ${savedLabel}` : 'Not written yet'}
        </p>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-400">
        What actually happened - decisions, actions, who owns what. Separate
        from the agenda above.
      </p>

      {canEdit ? (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                void save();
              }
            }}
            rows={6}
            maxLength={MEETING_NOTES_MAX}
            placeholder={'Decisions:\n- \n\nNext steps:\n- '}
            className="mt-2 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400">
              {text.length.toLocaleString()} / {MEETING_NOTES_MAX.toLocaleString()}
            </span>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!dirty || saving}
              className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-900 disabled:opacity-40"
            >
              {saving ? 'Saving...' : dirty ? 'Save notes' : 'Saved'}
            </button>
          </div>
        </>
      ) : event.notes ? (
        <p className="mt-2 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm text-slate-700 ring-1 ring-slate-200">
          {event.notes}
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-400">
          {event.ownerName} has not written any notes for this one.
        </p>
      )}

      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}

      {/* ---- the follow-up draft ---- */}
      <div className="mt-3 border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Follow-up
          </p>
          <button
            type="button"
            onClick={() => void makeDraft()}
            disabled={drafting || dirty}
            title={dirty ? 'Save your notes first - the draft is written from them' : undefined}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
          >
            {drafting ? 'Drafting...' : draft ? 'Redraft' : 'Draft follow-up'}
          </button>
        </div>

        {/* Saving first is not a nicety: the draft is generated server-side
            FROM THE SAVED NOTES, so drafting over unsaved text would quietly
            summarise the previous version. */}
        {dirty && (
          <p className="mt-1.5 text-[11px] text-slate-400">
            Save your notes first - the draft is written from them.
          </p>
        )}
        {draftError && <p className="mt-1.5 text-xs text-red-600">{draftError}</p>}

        {draft && (
          <div className="mt-2 rounded-lg bg-white p-3 ring-1 ring-slate-200">
            {!draft.basedOnNotes && (
              <p className="mb-2 rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 ring-1 ring-amber-100">
                Written without notes - only from the meeting's own details.
                Add notes and redraft for anything specific.
              </p>
            )}
            <p className="text-xs font-semibold text-slate-800">{draft.subject}</p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {draft.body}
            </p>
            <div className="mt-2.5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-400">
                A draft - check it before sending.
              </span>
              <button
                type="button"
                onClick={copy}
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
